/* eslint-disable ember/use-ember-get-and-set --
 * This service uses native `Map` for promise caches; the ember/use-ember-get-and-set
 * rule misfires on `.get`/`.set` and is unrelated to Ember reactivity.
 */
import Service, { service } from '@ember/service';
import { NotFoundError } from 'garaje/services/ajax/errors';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type { VisitorProfileStats } from 'garaje/types/visitor-profile-stats';
import urlBuilder from 'garaje/utils/url-builder';

interface VisitorProfileStatsResponse {
  data: {
    attributes: {
      'previous-sign-ins-count': number;
      'first-visit-date': string;
      'last-visit-date': string;
      'next-upcoming-visit-date-time': string;
      'upcoming-visits-count': {
        'recurring-count': number;
        'non-recurring-count': number;
      };
      'frequent-location-name'?: string | null;
      'frequent-host-id'?: string | null;
      'frequent-host-name'?: string | null;
    };
  };
}

interface VisitorProfileNoteResponse {
  data: {
    attributes: {
      note: string | null;
    };
  };
}

// Internal note result. `fromNotFound: true` means the resolution came from a
// 404 — the cache layer evicts those so a transient 404 (partial deploy,
// CDN→origin miss) doesn't poison the cache for the rest of the session. A
// successful 200 returning `note: null` is still cached.
interface NoteResult {
  note: string | null;
  fromNotFound: boolean;
}

// Cap each per-session cache so heavy back-office users (reception desks left
// open all day) don't accumulate entries unboundedly. 50 covers a typical
// browsing session of one screenful of rows; older entries fall out FIFO.
const CACHE_CAPACITY = 50;

// Reject the fetch if it hasn't resolved in this many ms. Without this, a hung
// network connection (origin returns no response, no error) would leave the
// cached promise permanently pending — every subsequent hover on the same
// profile id would attach to that hung promise and the hover card would be
// stuck on "Loading visit stats…" until page reload.
const FETCH_TIMEOUT_MS = 10_000;

// After a fetch rejects, suppress refetches for the same profile id for this
// long. Without this, hovering a row whose visit-stats endpoint 500s would
// fire a fresh request on every mouseenter — spamming the console with
// identical 500s on every cursor pass. The negative cache lets the failure
// clear after a short delay so a backend recovery is picked up on the next
// hover, while preventing per-hover request storms.
const FAILURE_BACKOFF_MS = 30_000;

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    p.then(
      (v) => {
        clearTimeout(id);
        resolve(v);
      },
      (err: unknown) => {
        clearTimeout(id);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

function trimToCapacity<K, V>(cache: Map<K, V>, capacity: number): void {
  while (cache.size > capacity) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }
}

/**
 * Lazy fetcher for visitor-profile visit stats AND the optional internal note.
 * Both endpoints are cached per-profile-id so multiple hovers / multiple cells
 * with the same profile share a single round trip each.
 *
 * Note semantics: a 404 from the notes endpoint legitimately means "no note
 * exists" and resolves to `null`. The cache only retains responses that
 * round-tripped successfully (including a successful 200 with `note: null`).
 * 404 resolutions are evicted so transient backend 404s can't permanently
 * mask a real note.
 */
export default class VisitStatsService extends Service {
  @service declare ajaxFetch: AjaxFetchService;

  #statsCache = new Map<string, Promise<VisitorProfileStats>>();
  #noteCache = new Map<string, Promise<NoteResult>>();
  // Per-key timestamp of the last failed fetch. While set, fetches for that
  // key short-circuit to a rejected promise without hitting the network —
  // suppresses console spam from repeatedly hovering a row whose endpoint
  // 500s. Cleared on entry expiry (FAILURE_BACKOFF_MS) so a backend recovery
  // is picked up automatically.
  #statsFailedAt = new Map<string, number>();
  #noteFailedAt = new Map<string, number>();

  fetch(profileId: string): Promise<VisitorProfileStats> {
    const cached = this.#statsCache.get(profileId);
    if (cached) return cached;

    if (this.#isInFailureBackoff(this.#statsFailedAt, profileId)) {
      return Promise.reject(new Error('visit-stats fetch suppressed by failure backoff'));
    }

    const promise = withTimeout(this.#fetchStats(profileId), FETCH_TIMEOUT_MS, 'visit-stats fetch');
    this.#statsCache.set(profileId, promise);
    trimToCapacity(this.#statsCache, CACHE_CAPACITY);
    promise.catch(() => {
      this.#statsCache.delete(profileId);
      this.#statsFailedAt.set(profileId, Date.now());
      trimToCapacity(this.#statsFailedAt, CACHE_CAPACITY);
    });
    return promise;
  }

  fetchNote(profileId: string): Promise<string | null> {
    const cached = this.#noteCache.get(profileId);
    if (cached) return cached.then((r) => r.note);

    if (this.#isInFailureBackoff(this.#noteFailedAt, profileId)) {
      // Resolve to null silently — keep the Internal note section hidden.
      // (Same user-visible result as a successful "no note" response, just
      // without re-hitting a backend that's currently failing.)
      return Promise.resolve(null);
    }

    const promise = withTimeout(this.#fetchNote(profileId), FETCH_TIMEOUT_MS, 'visit-stats note fetch');
    this.#noteCache.set(profileId, promise);
    trimToCapacity(this.#noteCache, CACHE_CAPACITY);
    promise
      .then((r) => {
        if (r.fromNotFound) this.#noteCache.delete(profileId);
      })
      .catch(() => {
        this.#noteCache.delete(profileId);
        this.#noteFailedAt.set(profileId, Date.now());
        trimToCapacity(this.#noteFailedAt, CACHE_CAPACITY);
      });
    return promise.then((r) => r.note);
  }

  #isInFailureBackoff(failureMap: Map<string, number>, profileId: string): boolean {
    const failedAt = failureMap.get(profileId);
    if (failedAt === undefined) return false;
    if (Date.now() - failedAt < FAILURE_BACKOFF_MS) return true;
    // Backoff window elapsed — clear and let the next caller try again.
    failureMap.delete(profileId);
    return false;
  }

  /**
   * Drop a profile's note from the cache so the next hover refetches.
   * Called by surfaces that mutate notes (e.g. ProfileNotes save) to avoid
   * stale data after edits.
   */
  invalidateNote(profileId: string): void {
    this.#noteCache.delete(profileId);
  }

  async #fetchStats(profileId: string): Promise<VisitorProfileStats> {
    const response = await this.ajaxFetch.request<VisitorProfileStatsResponse>(
      urlBuilder.v3.visitorProfiles.visitStats(profileId),
      {
        headers: { Accept: 'application/vnd.api+json' },
      },
    );
    const a = response.data.attributes;
    return {
      previousSignInsCount: a['previous-sign-ins-count'],
      firstVisitDate: a['first-visit-date'],
      lastVisitDate: a['last-visit-date'],
      nextUpcomingVisitDateTime: a['next-upcoming-visit-date-time'],
      upcomingVisitsCount: {
        recurringCount: a['upcoming-visits-count']['recurring-count'],
        nonRecurringCount: a['upcoming-visits-count']['non-recurring-count'],
      },
      frequentLocationName: a['frequent-location-name'] ?? null,
      frequentHostId: a['frequent-host-id'] ?? null,
      frequentHostName: a['frequent-host-name'] ?? null,
    };
  }

  async #fetchNote(profileId: string): Promise<NoteResult> {
    try {
      const response = await this.ajaxFetch.request<VisitorProfileNoteResponse>(
        urlBuilder.v3.visitorProfiles.note(profileId),
        {
          headers: { Accept: 'application/vnd.api+json' },
        },
      );
      const note = response.data.attributes.note;
      return {
        note: note && note.trim().length > 0 ? note : null,
        fromNotFound: false,
      };
    } catch (err: unknown) {
      // ajax-fetch throws typed `NotFoundError` on 404. The public contract
      // is `string | null`, so 404 maps to `null` — but tag the result so
      // the cache layer evicts it (vs cacheable success-with-null).
      if (err instanceof NotFoundError) return { note: null, fromNotFound: true };
      throw err;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'visit-stats': VisitStatsService;
  }
}
